import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const services = [
  {
    title: "कॉर्पोरेट हाउसकीपिंग",
    en: "Daily Housekeeping",
    desc: "दैनिक पोछा, वैक्यूमिंग, डस्टिंग, वॉशरूम सैनिटाइजेशन और कचरा प्रबंधन।",
    span: "lg:col-span-2",
    image:
      "https://images.unsplash.com/photo-1669101602108-fa5ba89507ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBqYW5pdG9yfGVufDB8fHx8MTc4NzM3NTQ3MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "डीप क्लीनिंग व स्क्रबिंग",
    en: "Deep Cleaning",
    desc: "सिंगल डिस्क फ्लोर मशीनों द्वारा फ्लोर स्क्रबिंग, पॉलिशिंग व टाइल्स क्लीनिंग।",
    span: "",
    image:
      "https://images.unsplash.com/photo-1522918279596-eb92d4d75259?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHx3YXRlciUyMHNwbGFzaCUyMHRleHR1cmV8ZW58MHx8fHwxNzg3Mzc1NDc3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "कारपेट व सोफा केयर",
    en: "Carpet & Upholstery",
    desc: "औद्योगिक वैक्यूम व शैम्पू एक्सट्रैक्शन विधि से कालीन और सोफा ड्राई क्लीनिंग।",
    span: "",
    image:
      "https://images.unsplash.com/photo-1580256081112-e49377338b7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjYXJwZXQlMjBjbGVhbmluZ3xlbnwwfHx8fDE3ODczNzU0Njl8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "फैसाड व ग्लास क्लीनिंग",
    en: "Facade & Glass Work",
    desc: "हाई-राइज़ ग्लास विंडोज़ और बाहरी ग्लास पैनल्स की विशेषज्ञ सफाई।",
    span: "lg:col-span-2",
    image: "https://images.pexels.com/photos/16898976/pexels-photo-16898976.jpeg",
  },
];

const ServiceCard = ({ s, i }) => (
  <motion.button
    data-testid={`service-card-${i}`}
    onClick={() => scrollToSection("#booking")}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative overflow-hidden border border-white/10 bg-[#14242F] text-left min-h-[320px] md:min-h-[380px] flex flex-col justify-end ${s.span}`}
  >
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={s.image}
        alt={s.en}
        className="w-full h-full object-cover opacity-40 grayscale-[30%] transition-all duration-700 ease-out group-hover:opacity-60 group-hover:scale-105 group-hover:grayscale-0"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0C1B24] via-[#0C1B24]/40 to-transparent" />
    </div>
    <div className="relative p-8 md:p-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-spacemono text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase">
            0{i + 1} — {s.en}
          </p>
          <h3 className="mt-3 font-khand font-semibold text-3xl md:text-4xl leading-tight text-white">
            {s.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-slate-300 font-mukta max-w-md leading-relaxed">
            {s.desc}
          </p>
        </div>
        <span className="shrink-0 w-11 h-11 border border-white/20 flex items-center justify-center transition-colors group-hover:bg-[#00F0FF] group-hover:border-[#00F0FF]">
          <ArrowUpRight className="w-5 h-5 text-white transition-colors group-hover:text-[#0C1B24]" />
        </span>
      </div>
    </div>
  </motion.button>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#0C1B24]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <SectionLabel>[ 02 — हमारी मुख्य सेवाएं / SERVICES ]</SectionLabel>
          <h2 className="mt-4 font-khand font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight">
            हर सतह, <span className="text-[#00F0FF]">बेदाग़ साफ़</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm md:text-base text-slate-400 font-mukta">
          चार प्रमुख सेवाएं — हर एक के पीछे प्रशिक्षित स्टाफ, प्रोफेशनल मशीनें और इको-फ्रेंडली केमिकल।
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s, i) => (
          <ServiceCard key={s.en} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);
